import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { PackageSearch, Search } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { ErrorState } from '../../components/ui/ErrorState';
import { clearSearch } from '../../features/tracking/trackingSlice';
import { searchPartnerOrders } from '../../features/tracking/trackingThunks';
import { TrackingField } from './components/TrackingField';
import { TrackingLayout } from './components/TrackingLayout';
import { TrackingResults } from './components/TrackingResults';

/**
 * GlobalTracking's admin. Fixed, as it has always been: this address belongs to
 * that partner and to no other, and there is no UI that could change it.
 */
const GLOBAL_TRACKING_ADMIN_ID = 10;

/**
 * The same lookup, under GlobalTracking's name.
 *
 * The difference from `Tracking` is the pairing: here the reference is scoped
 * to the partner instead of to the buyer's phone, so it is the only thing asked
 * for — and the only thing keeping one of their customers out of another's
 * order.
 */
export const TrackingGT = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { results, hasSearched, isSearching, searchError } = useSelector(
    (state) => state.tracking
  );

  const [idReference, setIdReference] = useState('');
  const [validationError, setValidationError] = useState('');

  useEffect(() => {
    dispatch(clearSearch());
  }, [dispatch]);

  const handleSubmit = (event) => {
    event.preventDefault();
    setValidationError('');

    if (!idReference.trim()) {
      setValidationError('Escribe el número de referencia.');
      return;
    }

    dispatch(
      searchPartnerOrders({
        idReference: idReference.trim(),
        idAdmin: GLOBAL_TRACKING_ADMIN_ID,
      })
    );
  };

  const error = validationError || searchError;

  return (
    <TrackingLayout
      testId="tracking-gt-page"
      brand="GlobalTracking"
      title="Sigue tu pedido"
      subtitle="Ingresa el número de referencia que te entregó GlobalTracking."
    >
      <div className="bg-white dark:bg-[#1a1a1a] rounded-2xl shadow-sm border border-border p-6 sm:p-8">
        <form onSubmit={handleSubmit} data-testid="tracking-search-form" className="space-y-5">
          <TrackingField
            id="tracking-gt-reference"
            testId="tracking-reference-input"
            label="Número de referencia"
            placeholder="Ej: 78910"
            value={idReference}
            onChange={setIdReference}
          />

          <Button
            type="submit"
            variant="primary"
            icon={Search}
            loading={isSearching}
            loadingLabel="Buscando..."
            fullWidth
          >
            Buscar
          </Button>
        </form>

        {error && <ErrorState variant="inline" title={error} className="mt-5" />}
      </div>

      {results.length > 0 && (
        <TrackingResults results={results} onSelect={(id) => navigate(`/track-order/${id}`)} />
      )}

      {hasSearched && !isSearching && !error && results.length === 0 && (
        <div
          data-testid="tracking-empty"
          className="mt-8 py-10 flex flex-col items-center justify-center text-center"
        >
          <div className="w-16 h-16 bg-ninja-gray dark:bg-[#2d2d2d] rounded-full flex items-center justify-center mb-4">
            <PackageSearch size={24} className="text-text-muted" />
          </div>
          <h2 className="text-base font-bold text-ninja-dark dark:text-white mb-2">
            No encontramos pedidos
          </h2>
          <p className="text-text-secondary max-w-sm">
            Revisa el número de referencia e inténtalo otra vez.
          </p>
        </div>
      )}
    </TrackingLayout>
  );
};
